import React from 'react';
import { useAuth } from './AuthContext';

const icons = {
  fleet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
  tagout: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  spare: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
  requests: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  inspect: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  reports: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  import: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  models: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  servicelog: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  more: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>,
};

// ─── SIDEBAR (desktop + mobile drawer) ──────────────────────────────────────

export default function Sidebar({ active, setActive, pendingCount, isOpen, onClose }) {
  const { profile, isAdmin, isManager, isExecutive, isTeamMember } = useAuth();

  const navItem = (id, label, badge) => (
    <button
      key={id}
      className={`nav-item${active === id ? ' active' : ''}`}
      onClick={() => { setActive(id); onClose?.(); }}
    >
      {icons[id]}
      {label}
      {badge > 0 && <span className="nav-badge">{badge}</span>}
    </button>
  );

  return (
    <nav className={`sidenav${isOpen ? ' open' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* INVENTORY */}
        <div className="nav-section-label">Inventory</div>
        {navItem('fleet', 'Fleet inventory')}
        {navItem('tagout', 'Tag-out log')}
        {!isTeamMember && navItem('spare', 'Spare pool')}

        {/* OPERATIONS */}
        {!isTeamMember && <>
          <div className="nav-section-label">Operations</div>
          {navItem('requests', 'Spare requests', pendingCount)}
          {isManager && navItem('servicelog', 'Service log')}
        </>}

        {/* INSPECTIONS */}
        <div className="nav-section-label">Inspections</div>
        {navItem('inspect', 'Inspections')}

        {/* REPORTS */}
        {(isManager || isExecutive) && <>
          <div className="nav-section-label">Reports</div>
          {navItem('reports', 'Reports')}
        </>}

        {/* ADMIN */}
        {isAdmin && <>
          <div className="nav-section-label">Admin</div>
          {navItem('users', 'User management')}
          {navItem('import', 'Import data')}
          {navItem('models', 'Model management')}
        </>}

      </div>

      {/* Profile name — desktop sidebar only */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #E8E8E8',
        marginTop: '8px',
      }}>
        <div style={{ fontSize: '11px', color: 'var(--gray-lt)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          {profile?.full_name}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--green-dk)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
          {profile?.role?.replace('_', ' ')}
        </div>
      </div>
    </nav>
  );
}

// ─── BOTTOM NAV (mobile only) ────────────────────────────────────────────────
//
// Tab selection logic by role:
//   team_member  → Fleet | Tag-out | Inspect | [More if needed]
//   manager      → Fleet | Tag-out | Requests | Inspect | Reports  (+More for service log)
//   executive    → Fleet | Inspect | Reports | [More]
//   admin        → Fleet | Tag-out | Requests | Inspect | More
//
// "More" opens the sidebar drawer for anything that doesn't fit.

export function BottomNav({ active, setActive, pendingCount, onMoreClick }) {
  const { isAdmin, isManager, isExecutive, isTeamMember } = useAuth();

  // Build the tab list based on role — max 5 items including More
  let tabs = [];

  if (isTeamMember) {
    tabs = [
      { id: 'fleet',   label: 'Fleet' },
      { id: 'tagout',  label: 'Tag-out' },
      { id: 'inspect', label: 'Inspect' },
    ];
  } else if (isExecutive) {
    tabs = [
      { id: 'fleet',   label: 'Fleet' },
      { id: 'inspect', label: 'Inspect' },
      { id: 'reports', label: 'Reports' },
    ];
  } else if (isManager) {
    // managers get 5 tabs; service log lives in the drawer (More)
    tabs = [
      { id: 'fleet',    label: 'Fleet' },
      { id: 'tagout',   label: 'Tag-out' },
      { id: 'requests', label: 'Requests', badge: pendingCount },
      { id: 'inspect',  label: 'Inspect' },
      { id: 'reports',  label: 'Reports' },
    ];
  } else if (isAdmin) {
    // admins: 4 core tabs + More (admin pages live in drawer)
    tabs = [
      { id: 'fleet',    label: 'Fleet' },
      { id: 'tagout',   label: 'Tag-out' },
      { id: 'requests', label: 'Requests', badge: pendingCount },
      { id: 'inspect',  label: 'Inspect' },
    ];
  }

  // Determine if we need a More button
  // Managers fill all 5 slots — no More needed
  // Everyone else gets More if there are items in the drawer
  const needsMore = !isManager;

  return (
    <nav className="bottom-nav" role="navigation" aria-label="Main navigation">
      <div className="bottom-nav-inner">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`bottom-nav-item${active === tab.id ? ' active' : ''}`}
            onClick={() => setActive(tab.id)}
            aria-label={tab.label}
          >
            {icons[tab.id]}
            <span>{tab.label}</span>
            {tab.badge > 0 && (
              <span className="bottom-nav-badge">{tab.badge > 9 ? '9+' : tab.badge}</span>
            )}
          </button>
        ))}

        {needsMore && (
          <button
            className={`bottom-nav-item${['users','import','models','servicelog','spare'].includes(active) ? ' active' : ''}`}
            onClick={onMoreClick}
            aria-label="More"
          >
            {icons.more}
            <span>More</span>
          </button>
        )}
      </div>
    </nav>
  );
}